'use client';

import { useState } from 'react';
import { useRouter } from '@/i18n/routing';
import { createTestimonial, updateTestimonial } from "@/lib/actions/testimonials";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import { toast } from "sonner";
import { Save, ArrowLeft, Quote, User, Building2, ListOrdered } from "lucide-react";
import { Link } from '@/i18n/routing';

import { RichTextEditor } from "../shared/RichTextEditor";

interface TestimonialFormProps {
  initialData?: any;
}

export function TestimonialForm({ initialData }: TestimonialFormProps) {
  const router = useRouter();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: initialData?.name || '',
    company: initialData?.company || '',
    message: initialData?.message || '',
    position: initialData?.position || '',
    order: initialData?.order || 0,
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleManualChange = (name: string, value: string) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const result = initialData
        ? await updateTestimonial(initialData.id, formData)
        : await createTestimonial(formData);

      if (result.success) {
        toast.success(`Testimonial ${initialData ? 'updated' : 'created'} successfully`);
        router.push('/admin/testimonials');
      } else {
        toast.error(result.error || "Something went wrong");
      }
    } catch (error) {
      toast.error("Failed to save testimonial");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" asChild>
          <Link href="/admin/testimonials">
            <ArrowLeft className="h-5 w-5" />
          </Link>
        </Button>
        <h1 className="text-3xl font-bold text-navy">
          {initialData ? 'Edit Testimonial' : 'New Testimonial'}
        </h1>
      </div>

      <form onSubmit={handleSubmit}>
        <Card className="shadow-sm border-0">
          <CardHeader className="bg-light/30 border-b">
            <div className="flex items-center gap-2">
              <Quote className="h-5 w-5 text-primary" />
              <div>
                <CardTitle>Testimonial Details</CardTitle>
                <CardDescription>Fill in the client's review and information.</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6 pt-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="name" className="flex items-center gap-2">
                  <User size={14} className="text-gray-400" /> Client Name
                </Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="e.g. John Doe"
                  required
                  className="rounded-xl border-gray-200"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="position">Position / Job Title</Label>
                <Input
                  id="position"
                  name="position"
                  value={formData.position}
                  onChange={handleChange}
                  placeholder="e.g. CEO or Director"
                  className="rounded-xl border-gray-200"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="company" className="flex items-center gap-2">
                <Building2 size={14} className="text-gray-400" /> Company Name
              </Label>
              <Input
                id="company"
                name="company"
                value={formData.company}
                onChange={handleChange}
                placeholder="e.g. Acme Corp"
                className="rounded-xl border-gray-200"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="message">Message / Testimonial</Label>
              <RichTextEditor value={formData.message} onChange={(val) => handleManualChange('message', val)} />
            </div>

            <div className="w-1/3 space-y-2">
              <Label htmlFor="order" className="flex items-center gap-2">
                <ListOrdered size={14} className="text-gray-400" /> Display Order
              </Label>
              <Input
                id="order"
                name="order"
                type="number"
                value={formData.order}
                onChange={handleChange}
                className="rounded-xl border-gray-200"
              />
            </div>
          </CardContent>
          <CardFooter className="bg-gray-50/30 border-t flex justify-end gap-3 pt-6 pb-6">
            <Button variant="outline" asChild className="rounded-xl px-6" disabled={isLoading}>
              <Link href="/admin/testimonials">Cancel</Link>
            </Button>
            <Button type="submit" className="bg-navy hover:bg-navy/90 rounded-xl px-8 transition-all" disabled={isLoading}>
              {isLoading ? 'Saving...' : <><Save className="mr-2 h-4 w-4" /> Save Testimonial</>}
            </Button>
          </CardFooter>
        </Card>
      </form>
    </div>
  );
}
